import { ShapeTypeEnum } from '../../../../../enum/workbench/shape-type.enum';
import { ConfigurationPlan } from '../../../../../models/configurators-2d/applications/workbench/configurations/configuration-plan';

export type WorkbenchSide = 'top' | 'bottom' | 'left' | 'right';

export interface WorkbenchSideSegment {
  start: { x: number, y: number };
  end: { x: number, y: number };
}

export function getWorkbenchSideSegment(
  plan: ConfigurationPlan,
  side: WorkbenchSide
): WorkbenchSideSegment | null {
  const L = plan.length;
  const W = plan.width;

  switch (plan.shape.shapeType) {
    case ShapeTypeEnum.Rectangle:
      return getRectangleSegment(L, W, side);
    case ShapeTypeEnum.HalfMoon: {
      const r = W / 2;
      if (side === 'left') return { start: { x: 0, y: W }, end: { x: 0, y: 0 } };
      if (side === 'bottom') return { start: { x: 0, y: 0 }, end: { x: L - r, y: 0 } };
      if (side === 'top') return { start: { x: 0, y: W }, end: { x: L - r, y: W } };
      return null;
    }
    case ShapeTypeEnum.Mushroom: {
      const diameter = Number(plan.diameter || plan.shape?.['diameter'] || (plan as any).shapeDiameter) || W;
      const radius = diameter / 2;
      const adjX = Math.sqrt(Math.max(0, radius ** 2 - (W - radius) ** 2));

      if (side === 'left') return { start: { x: 0, y: W }, end: { x: 0, y: 0 } };
      if (side === 'bottom') return { start: { x: 0, y: 0 }, end: { x: L - radius, y: 0 } };
      if (side === 'top') return { start: { x: 0, y: W }, end: { x: L - radius - adjX, y: W } };
      return null;
    }
    case ShapeTypeEnum.Angle:
      if (side === 'left') return { start: { x: 0, y: L }, end: { x: 0, y: 0 } };
      if (side === 'bottom') return { start: { x: 0, y: 0 }, end: { x: W, y: 0 } };
      if (side === 'right') return { start: { x: L, y: L - W }, end: { x: L, y: L } };
      if (side === 'top') return { start: { x: L, y: L }, end: { x: 0, y: L } };
      return null;
    default:
      return null;
  }
}

export function inferWorkbenchSideFromSegment(
  plan: ConfigurationPlan,
  startPoint: { x: number, y: number } | null | undefined,
  endPoint: { x: number, y: number } | null | undefined,
  tolerance = 2
): WorkbenchSide | null {
  if (!startPoint || !endPoint) return null;

  const sides: WorkbenchSide[] = ['top', 'bottom', 'left', 'right'];
  return (
    sides.find(side => {
      const segment = getWorkbenchSideSegment(plan, side);
      if (!segment) return false;

      return (
        isSamePoint(startPoint, segment.start, tolerance) &&
        isSamePoint(endPoint, segment.end, tolerance)
      ) || (
        isSamePoint(startPoint, segment.end, tolerance) &&
        isSamePoint(endPoint, segment.start, tolerance)
      );
    }) || null
  );
}

function getRectangleSegment(length: number, width: number, side: WorkbenchSide): WorkbenchSideSegment {
  if (side === 'top') return { start: { x: length, y: width }, end: { x: 0, y: width } };
  if (side === 'bottom') return { start: { x: 0, y: 0 }, end: { x: length, y: 0 } };
  if (side === 'left') return { start: { x: 0, y: width }, end: { x: 0, y: 0 } };
  return { start: { x: length, y: 0 }, end: { x: length, y: width } };
}

function isSamePoint(
  first: { x: number, y: number },
  second: { x: number, y: number },
  tolerance: number
): boolean {
  return Math.abs(first.x - second.x) < tolerance && Math.abs(first.y - second.y) < tolerance;
}
